import React from "react";
import InputMask from "react-input-mask";
import {
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  FormControl,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import DeleteIcon from "@mui/icons-material/Delete";
import { normalizeUsState } from "../../../../../globals/utils/usStates";
import GoogleAutoComplete from "./GoogleAutoComplete";
import CustomCreatableSelect from "./CustomCreatableSelect";

function LocationsStep({
  locations,
  addressFieldErrors,
  handleLocationChange,
  handleGoogleCallback,
  handleZipCodeChange,
  allCities,
  allStates,
  allCounties,
  removeLocation,
}) {
  return (
    <>
      {locations.map((location, index) => (
        <Box
          key={location.law_firm_location_id ?? `${location.name}-${location.address}-${location.customerNumber}`}
          sx={{
            padding: 2,
            marginBottom: 3,
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Location Name"
                name="name"
                fullWidth
                variant="outlined"
                value={location.name}
                onChange={(e) =>
                  handleLocationChange(index, "name", e.target.value)
                }
                sx={{ marginTop: 2, marginBottom: 2 }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <GoogleAutoComplete
                label="Search Address"
                placeholder="Search for an address (e.g., 600 Brickell Ave)"
                callback={(address, city, state, county, zipCode) =>
                  handleGoogleCallback(
                    index,
                    address,
                    city,
                    state,
                    county,
                    zipCode
                  )
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                label="Address"
                name="address"
                fullWidth
                variant="outlined"
                value={location.address}
                onChange={(e) =>
                  handleLocationChange(index, "address", e.target.value)
                }
                error={!!addressFieldErrors[index]?.address}
                helperText={
                  addressFieldErrors[index]?.address ? "Required" : ""
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Zip Code"
                name="zipCode"
                fullWidth
                required
                variant="outlined"
                value={location.zipCode}
                onChange={(e) => handleZipCodeChange(index, e.target.value)}
                error={!!addressFieldErrors[index]?.zipCode}
                helperText={
                  addressFieldErrors[index]?.zipCode ? "Required" : ""
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <CustomCreatableSelect
                label="City"
                options={allCities.map((city) => ({
                  label: city,
                  value: city,
                }))}
                value={
                  location.city
                    ? { label: location.city, value: location.city }
                    : null
                }
                onChange={(opt) =>
                  handleLocationChange(index, "city", opt ? opt.value : "")
                }
                placeholder="Select a city"
                error={!!addressFieldErrors[index]?.city}
              />
              {addressFieldErrors[index]?.city && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: -1, mb: 1, display: "block" }}
                >
                  Required
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <CustomCreatableSelect
                label="State"
                options={allStates.map((st) => ({
                  label: st,
                  value: st,
                }))}
                value={
                  location.state
                    ? { label: location.state, value: location.state }
                    : null
                }
                onChange={(opt) => {
                  const next = normalizeUsState(opt ? opt.value : "", allStates);
                  handleLocationChange(index, "state", next);
                }}
                placeholder="Select a state"
                required
                error={!!addressFieldErrors[index]?.state}
              />
              {addressFieldErrors[index]?.state && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: -1, mb: 1, display: "block" }}
                >
                  Required
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }} sx={{ mt: 0 }}>
              <FormControl fullWidth error={!!addressFieldErrors[index]?.county}>
                <CustomCreatableSelect
                  label="County"
                  options={allCounties.map((county) => ({
                    label: county,
                    value: county,
                  }))}
                  value={
                    location.county
                      ? {
                          label: location.county,
                          value: location.county,
                        }
                      : null
                  }
                  onChange={(opt) =>
                    handleLocationChange(
                      index,
                      "county",
                      opt ? opt.value : ""
                    )
                  }
                  placeholder="Select or type a county"
                  error={!!addressFieldErrors[index]?.county}
                />
                {addressFieldErrors[index]?.county && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: -1, mb: 1, display: "block" }}
                  >
                    Required
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <InputMask
                mask="(999) 999-9999"
                value={location.phone}
                onChange={(e) =>
                  handleLocationChange(index, "phone", e.target.value)
                }
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    label="Phone"
                    name="phone"
                    fullWidth
                    variant="outlined"
                  />
                )}
              </InputMask>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Fax"
                name="fax"
                fullWidth
                variant="outlined"
                value={location.fax}
                onChange={(e) =>
                  handleLocationChange(index, "fax", e.target.value)
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                required
                label="Customer Number"
                name="customerNumber"
                fullWidth
                variant="outlined"
                value={location.customerNumber}
                onChange={(e) =>
                  handleLocationChange(
                    index,
                    "customerNumber",
                    e.target.value
                  )
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={location.useSeparateAccounting}
                    onChange={() =>
                      handleLocationChange(
                        index,
                        "useSeparateAccounting",
                        !location.useSeparateAccounting
                      )
                    }
                  />
                }
                label="Use separate accounting contact for this location"
              />
            </Grid>
            {location.useSeparateAccounting && (
              <>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Accounting Contact Name"
                    name="name"
                    fullWidth
                    variant="outlined"
                    value={location.accountingContact.name}
                    onChange={(e) =>
                      handleLocationChange(index, "accountingContact", {
                        ...location.accountingContact,
                        name: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Accounting Contact Email"
                    name="email"
                    fullWidth
                    variant="outlined"
                    value={location.accountingContact.email}
                    onChange={(e) =>
                      handleLocationChange(index, "accountingContact", {
                        ...location.accountingContact,
                        email: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Accounting Contact Phone"
                    name="phone"
                    fullWidth
                    variant="outlined"
                    value={location.accountingContact.phone}
                    onChange={(e) =>
                      handleLocationChange(index, "accountingContact", {
                        ...location.accountingContact,
                        phone: e.target.value,
                      })
                    }
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={location.addressTypes.physical}
                    onChange={() =>
                      handleLocationChange(index, "addressTypes", {
                        ...location.addressTypes,
                        physical: !location.addressTypes.physical,
                      })
                    }
                  />
                }
                label="Physical Address"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={location.addressTypes.mailing}
                    onChange={() =>
                      handleLocationChange(index, "addressTypes", {
                        ...location.addressTypes,
                        mailing: !location.addressTypes.mailing,
                      })
                    }
                  />
                }
                label="Mailing Address"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={location.addressTypes.billing}
                    onChange={() =>
                      handleLocationChange(index, "addressTypes", {
                        ...location.addressTypes,
                        billing: !location.addressTypes.billing,
                      })
                    }
                  />
                }
                label="Billing Address"
              />
            </Grid>
            {locations.length > 1 && (
              <Grid size={{ xs: 12 }} sx={{ textAlign: "right" }}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => removeLocation(index)}
                  sx={{ marginTop: 2 }}
                >
                  Remove Location
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      ))}
    </>
  );
}

export default LocationsStep;
